import { useState, useEffect } from 'react';
import { Box, Fab, Tooltip, Zoom } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EvolucionPartidosModal from '../modals/EvolucionPartidosModal';
import BalonParadoModal from '../modals/BalonParadoModal';
import EstadoPlantillaModal from '../modals/EstadoPlantillaModal';
import ProximosPartidosModal from '../modals/ProximosPartidosModal';
import CustomImageComponent from '@/components/shared/custom/custom-image';
import { getBalonParadoImage } from '@/helpers/images-helper';
import EvolucionPartidoIcon from '@/components/shared/icons/EvolucionPartidoIcon';
import InfoIcon from '@/components/shared/icons/info-icon';
import PersonIcon from '@/components/shared/SearchBarComponent/PersonIcon';
import CalendarioFantasyAdminIcon from '@/components/shared/icons/calendario-fantasy-admin';

const TeamStatsFloatingMenu = ({ teamId, iconSize = 24, bottom, top, right, left }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [modalOpen, setModalOpen] = useState(null);
    const [isAnimating, setIsAnimating] = useState(false);

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    const handleOptionClick = (optionId) => {
        setIsOpen(false);
        setModalOpen(optionId);
    };

    const handleCloseModal = () => {
        setModalOpen(null);
    };

    // Animación sutil cada 20 segundos
    useEffect(() => {
        const interval = setInterval(() => {
            setIsAnimating(true);
            setTimeout(() => setIsAnimating(false), 1000); // Duración de la animación: 1 segundo
        }, 20000); // Cada 20 segundos

        return () => clearInterval(interval);
    }, []);

    const options = [
        {
            id: 'progression',
            label: 'Evolución de Partidos',
            icon: <EvolucionPartidoIcon />,
            action: () => handleOptionClick('progression'),
        },
        {
            id: 'balon-parado',
            label: 'Balón Parado',
            icon: (
                <CustomImageComponent
                    src={getBalonParadoImage(45)}
                    alt="Balón Parado"
                    width={30}
                    height={30}
                />
            ),
            action: () => handleOptionClick('balon-parado'),
        },
        {
            id: 'plantilla',
            label: 'Estado de Plantilla',
            icon: <PersonIcon width={30} height={30} />,
            action: () => handleOptionClick('plantilla'),
        },
        {
            id: 'proximos-partidos',
            label: 'Próximos Partidos',
            icon: <CalendarioFantasyAdminIcon iconWidth={30} iconHeight={30} />,
            action: () => handleOptionClick('proximos-partidos'),
        },
    ];

    // Calcular posición en arco para cada opción
    const getArcPosition = (index, total) => {
        const radius = 80; // Radio del arco (más pegado al botón)
        const startAngle = 180; // Ángulo inicial (180° = izquierda horizontal)
        const endAngle = 270; // Ángulo final (270° = abajo vertical)
        const angleStep = (endAngle - startAngle) / (total - 1);
        const angle = startAngle + angleStep * index;
        const radian = (angle * Math.PI) / 180;

        // Calcular posición usando trigonometría
        const x = Math.cos(radian) * radius;
        const y = Math.sin(radian) * radius;

        return {
            left: `${-x}px`, // Negativo para ir hacia la derecha
            top: `${-y}px`, // Negativo para ir hacia abajo
        };
    };

    return (
        <Box
            sx={{
                position: 'absolute',
                bottom: bottom,
                top: top,
                right: right,
                left: left,
                zIndex: 1000,
            }}
        >
            {/* Opciones flotantes en arco */}
            {options.map((option, index) => {
                const arcPosition = getArcPosition(index, options.length);
                return (
                    <Zoom
                        key={option.id}
                        in={isOpen}
                        style={{
                            transitionDelay: isOpen ? `${index * 50}ms` : '0ms',
                        }}
                        unmountOnExit
                    >
                        <Tooltip title={option.label} placement="left">
                            <Fab
                                size="small"
                                onClick={option.action}
                                sx={{
                                    position: 'absolute',
                                    ...arcPosition,
                                    backgroundColor: 'transparent',
                                    color: '#fff',
                                    width: iconSize,
                                    height: iconSize,
                                    minHeight: iconSize,
                                    padding: 0,
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                        filter: 'brightness(1.2)',
                                        transform: 'scale(1.15)',
                                    },
                                    transition: 'all 0.2s ease',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                    '& .MuiSvgIcon-root': {
                                        fontSize: iconSize - 4,
                                        width: '100%',
                                        height: '100%',
                                    },
                                    '& img': {
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                    },
                                }}
                            >
                                {option.icon}
                            </Fab>
                        </Tooltip>
                    </Zoom>
                );
            })}

            {/* Botón principal */}
            <Fab
                size="medium"
                onClick={handleToggle}
                sx={{
                    position: 'relative',
                    backgroundColor: isOpen ? '#D81B60' : 'transparent',
                    width: iconSize,
                    height: iconSize,
                    minHeight: iconSize,
                    '&:hover': {
                        backgroundColor: isOpen ? '#AD1457' : '#a126da',
                    },
                    transition: 'all 0.3s ease',
                    transform: isOpen
                        ? 'rotate(90deg)'
                        : isAnimating
                          ? 'scale(1.1) rotate(360deg)'
                          : 'rotate(0deg)',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.4)',
                    '& .MuiSvgIcon-root': {
                        fontSize: 16,
                        transition: 'all 0.3s ease',
                    },
                }}
            >
                {isOpen ? <CloseIcon /> : <InfoIcon />}
            </Fab>

            {/* Modales */}
            <EvolucionPartidosModal
                open={modalOpen === 'progression'}
                onClose={handleCloseModal}
                teamId={teamId}
            />

            <BalonParadoModal
                open={modalOpen === 'balon-parado'}
                onClose={handleCloseModal}
                teamId={teamId}
            />

            <EstadoPlantillaModal
                open={modalOpen === 'plantilla'}
                onClose={handleCloseModal}
                teamId={teamId}
            />

            <ProximosPartidosModal
                open={modalOpen === 'proximos-partidos'}
                onClose={handleCloseModal}
                teamId={teamId}
            />
        </Box>
    );
};

export default TeamStatsFloatingMenu;
